import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  CASAS_PERCENTUAL,
  CASAS_QUANTIDADE,
  desescalar,
  dinheiroProporcional,
  dividirArredondando,
  escalar,
  somarDinheiro,
} from './aritmetica.ts';
import { dinheiroSchema, percentualSchema } from './comum.ts';
import type { TipoInsumo } from './insumos.ts';

/**
 * A tributação que o fornecedor destaca — **um desenho, três documentos**.
 *
 * Nasceu na proposta da cotação e vivia lá dentro. Saiu para cá quando o pedido
 * de compra recebeu o mesmo desenho, e a mudança fecha um débito que estava
 * escrito no código: a geração de pedidos tinha uma função de tradução entre os
 * dois formatos, com o comentário "é temporária: o pedido e a nota recebem o
 * mesmo desenho depois, e aí ela sai inteira". Saiu.
 *
 * O que se ganha não é linha de código economizada, é a **conta**. Enquanto o
 * pedido guardava só o valor de cada imposto, a proposta que cobria 60 dos 100
 * sacos ofertados virava pedido com o imposto de 100 — ou com regra de três
 * sobre um número já arredondado, que erra centavos e não sabe dizer quanto
 * errou. Com base e alíquota gravadas nos dois documentos, o pedido refaz a
 * conta sobre a base nova e fecha exata. Ver `impostosProporcionais`.
 *
 * Três ideias organizam o módulo:
 *
 * - **Cada imposto é uma trinca: base, alíquota e valor.** O valor sozinho não
 *   se proporcionaliza nem se confere; a alíquota sozinha não bate com o papel
 *   do fornecedor, que arredonda. As três juntas dizem o que foi cobrado *e*
 *   por quê.
 * - **Dois conjuntos, decididos pelo tipo do insumo.** Material tem IPI, ICMS,
 *   ICMS ST e crédito do Simples; serviço tem ISS, IRF, INSS e as retenções
 *   federais; IBS e CBS valem nos dois. Perguntar ISS numa compra de cimento
 *   seria oferecer um campo que nunca se preenche.
 * - **A estrutura dos blocos é dado, não desenho de tela.** `BLOCOS_IMPOSTO_*`
 *   diz qual base pertence a qual alíquota, e é isso que permite a tela
 *   calcular enquanto se digita e o rateio proporcional refazer a conta — sem
 *   que nenhum dos dois precise reconstruir a relação a partir do nome do campo.
 *
 * **PIS e COFINS por dentro não estão aqui**, e a ausência é decisão: o crédito
 * deles não depende de quem vende, e sim do regime de quem compra — é conta da
 * nota de compra, não do que o fornecedor destacou. O Imposto Seletivo saiu
 * junto, pelo mesmo motivo de não aparecer em nenhuma das duas listas de
 * negócio.
 */

/* -------------------------------------------------------------------------- */
/* Os campos                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Valor de imposto: nunca negativo, e `'0'` em branco.
 *
 * Imposto negativo seria crédito, e crédito é outra coisa — ele nasce na nota de
 * compra, depende da operação fiscal e do regime da empresa, e é gravado em
 * colunas próprias lá. Aceitar o sinal invertido aqui deixaria o mesmo fato ser
 * digitado de duas formas.
 *
 * O default existe porque o caso comum é o comprador destacar dois ou três
 * impostos e deixar o resto em branco. Exigir a trinca completa de cada um seria
 * trabalho que não acrescenta informação.
 */
const valorImpostoSchema = dinheiroSchema
  .refine((v) => Number(v) >= 0, 'O imposto não pode ser negativo.')
  .prefault('0');

/** Alíquota em percentual, quatro casas. Nunca negativa — o schema não aceita sinal. */
const aliquotaSchema = percentualSchema.prefault('0');

/**
 * IBS e CBS — os únicos que valem nos **dois** tipos.
 *
 * Uma base para os dois, e não uma para cada: os dois incidem sobre o mesmo
 * valor da operação, e duas bases idênticas lado a lado seriam dois campos para
 * digitar o mesmo número.
 */
const impostosComuns = {
  valorBaseIbsCbs: valorImpostoSchema,
  aliquotaIbs: aliquotaSchema,
  /**
   * Redução da base do IBS, em percentual.
   *
   * Reduz o imposto na mesma proporção em que reduziria a base — as duas contas
   * dão o mesmo número —, e é gravada separada da alíquota de propósito: 18% com
   * 60% de redução e 7,2% cheios pagam igual, mas só o primeiro par diz **por
   * que** paga pouco, e é o que a nota vai destacar.
   */
  aliquotaReducaoIbs: aliquotaSchema,
  aliquotaCbs: aliquotaSchema,
  /** Redução da base da CBS, em percentual. Ver `aliquotaReducaoIbs`. */
  aliquotaReducaoCbs: aliquotaSchema,
  valorIbs: valorImpostoSchema,
  valorCbs: valorImpostoSchema,
};

/** Só em **material**. Zerados quando o insumo é serviço. */
const impostosDeMaterial = {
  valorBaseIpi: valorImpostoSchema,
  aliquotaIpi: aliquotaSchema,
  /** Cobrado por fora: soma ao que se paga, e por isso entra no custo previsto. */
  valorIpi: valorImpostoSchema,

  valorBaseIcms: valorImpostoSchema,
  aliquotaIcms: aliquotaSchema,
  /** Cobrado por dentro: já está embutido no valor da mercadoria. */
  valorIcms: valorImpostoSchema,

  /**
   * Crédito do Simples Nacional — **sem campo de base**.
   *
   * A base é o valor do item, sempre, e é o próprio fornecedor optante quem
   * informa o percentual que pode ser aproveitado. Um campo de base aqui seria
   * o valor do item copiado à mão, com a chance de divergir dele.
   */
  aliquotaCreditoSimples: aliquotaSchema,
  valorCreditoSimples: valorImpostoSchema,

  valorBaseIcmsSt: valorImpostoSchema,
  aliquotaIcmsSt: aliquotaSchema,
  /** Cobrado por fora, como o IPI. Entra no custo previsto. */
  valorIcmsSt: valorImpostoSchema,
};

/** Só em **serviço**. Zerados quando o insumo é material. */
const impostosDeServico = {
  valorBaseIss: valorImpostoSchema,
  aliquotaIss: aliquotaSchema,
  /** Quando o tomador retém, a alíquota da retenção é outra — daí os dois pares. */
  aliquotaIssRetido: aliquotaSchema,
  valorIss: valorImpostoSchema,
  valorIssRetido: valorImpostoSchema,

  valorBaseIrf: valorImpostoSchema,
  aliquotaIrf: aliquotaSchema,
  valorIrf: valorImpostoSchema,

  valorBaseInss: valorImpostoSchema,
  aliquotaInss: aliquotaSchema,
  valorInss: valorImpostoSchema,

  /**
   * A base das três retenções federais, e os três valores que saem dela.
   *
   * Uma base para PIS, COFINS e CSLL porque as três incidem sobre o mesmo
   * valor — é assim que a nota de serviço as destaca. Substituiu o
   * `valorRetencoes` somado, que registrava o total e perdia de quem era cada
   * parte: na hora de classificar a retenção no financeiro, "3.650,00 de
   * retenções" não diz o que recolher em cada guia.
   */
  valorBaseRetencoes: valorImpostoSchema,
  aliquotaRetencaoPis: aliquotaSchema,
  aliquotaRetencaoCofins: aliquotaSchema,
  aliquotaRetencaoCsll: aliquotaSchema,
  valorPisRetido: valorImpostoSchema,
  valorCofinsRetido: valorImpostoSchema,
  valorCsllRetido: valorImpostoSchema,
};

/**
 * Os campos **numéricos** — base, alíquota e valor de cada imposto.
 *
 * Separados da bandeira abaixo porque tudo o que percorre impostos genericamente
 * — a projeção do repository, o schema do formulário, o payload — trata string
 * decimal. Um booleano no meio da lista viraria `'0'` na conversão e `false` de
 * volta, sempre, e a bandeira nunca gravaria ligada.
 */
const impostosNumericos = {
  ...impostosDeMaterial,
  ...impostosDeServico,
  ...impostosComuns,
};

/**
 * Os impostos destacados, no payload de gravação.
 *
 * Um schema só para os dois conjuntos, e não um por tipo do insumo: quem edita é
 * o mesmo formulário, e o que decide quais campos aparecem é `blocosDeImposto`.
 * Os do outro conjunto ficam em branco na tela e vão zerados no payload, que é
 * exatamente o que "não se aplica" significa aqui.
 */
export const impostosDestacadosEntradaSchema = z.object({
  ...impostosNumericos,
  /**
   * O IPI entra na base de cálculo do ICMS?
   *
   * Depende da operação, não do produto: na venda a consumidor final o IPI compõe
   * a base do ICMS; na venda para industrialização ou revenda, não. Quem sabe é o
   * fornecedor, e é por isso que a resposta é dele — não dá para derivá-la de
   * nada que o sistema já tenha.
   *
   * Bandeira, e não um segundo campo de base digitado à mão: com ela, a base do
   * ICMS se refaz sozinha quando o IPI muda, e o documento não fica com dois
   * números que discordam sobre o mesmo fato.
   */
  ipiNaBaseDoIcms: z.boolean().prefault(false),
});

export type ImpostosDestacadosEntrada = z.infer<typeof impostosDestacadosEntradaSchema>;

/**
 * Os impostos como a API os devolve.
 *
 * **É o mesmo schema da entrada**, e não uma cópia sem os defaults. Eram duas
 * listas de campos idênticas mantidas à mão, e trinta e quatro campos em duas
 * cópias divergem na primeira coluna nova — a que faltasse aqui sumiria da
 * resposta sem nenhum erro de tipo. Os defaults não atrapalham na leitura: a
 * API preenche todos, e um campo ausente vira `'0'` em vez de derrubar a
 * resposta inteira.
 */
export const impostosDestacadosSchema = impostosDestacadosEntradaSchema;

export type ImpostosDestacados = ImpostosDestacadosEntrada;

/**
 * Os campos numéricos, e só eles.
 *
 * `ipiNaBaseDoIcms` fica de fora: quem percorre esta lista está convertendo,
 * validando ou projetando string decimal, e a bandeira é tratada pelo nome nos
 * poucos lugares que a conhecem.
 */
export type CampoDeImposto = keyof typeof impostosNumericos;

/* -------------------------------------------------------------------------- */
/* Os blocos — qual base pertence a qual alíquota                             */
/* -------------------------------------------------------------------------- */

/** Uma alíquota e o valor que ela produz sobre a base do bloco. */
export interface LinhaDeImposto {
  rotulo: string;
  aliquota: CampoDeImposto;
  /** Redução da base, em percentual. Só IBS e CBS têm. */
  reducao?: CampoDeImposto;
  valor: CampoDeImposto;
}

/**
 * Um imposto na tela: a base, e as alíquotas que incidem sobre ela.
 *
 * Estrutura, e não uma lista plana de campos, porque a relação entre os três
 * **é** a regra: é ela que a tela usa para calcular o valor e que o rateio
 * proporcional usa para refazer a conta. Uma lista plana obrigaria a reconstruir
 * em cada consumidor qual base pertence a qual alíquota, a partir do nome do
 * campo.
 */
export interface BlocoDeImposto {
  rotulo: string;
  /** Campo da base. `null` quando a base é o valor do item — ver crédito do Simples. */
  base: CampoDeImposto | null;
  /**
   * A base começa valendo o valor do item?
   *
   * Verdadeiro nos impostos que incidem sobre a mercadoria inteira — IPI, ICMS,
   * ICMS ST, IBS e CBS —, e é o que a tela usa para preencher o campo em vez de
   * fazer o comprador redigitar um número que já está no documento. Continua
   * editável: a base reduzida e a MVA do ICMS ST são justamente os casos em que
   * ela difere.
   *
   * Falso nos de serviço: ISS, IRF, INSS e as retenções têm base própria, que
   * quase nunca é o valor cheio da nota, e um palpite ali seria pior que o campo
   * vazio.
   */
  basePadraoEhValorDoItem: boolean;
  /** Ocupa a linha inteira na grade de dois blocos. Para os que têm campos demais. */
  linhaInteira?: boolean;
  linhas: readonly [LinhaDeImposto, ...LinhaDeImposto[]];
}

/** IBS e CBS, o bloco que os dois tipos compartilham. Fecha as duas listas. */
const BLOCO_IBS_CBS: BlocoDeImposto = {
  rotulo: 'IBS e CBS',
  base: 'valorBaseIbsCbs',
  basePadraoEhValorDoItem: true,
  // Sete campos: base, e alíquota, redução e valor de cada um dos dois.
  linhaInteira: true,
  linhas: [
    {
      rotulo: 'IBS',
      aliquota: 'aliquotaIbs',
      reducao: 'aliquotaReducaoIbs',
      valor: 'valorIbs',
    },
    {
      rotulo: 'CBS',
      aliquota: 'aliquotaCbs',
      reducao: 'aliquotaReducaoCbs',
      valor: 'valorCbs',
    },
  ],
};

/**
 * A ordem é a da tela, e ela desenha as três linhas que o comprador pediu:
 * IPI e ICMS, depois ICMS ST e Simples, e IBS/CBS ocupando a última inteira.
 */
export const BLOCOS_IMPOSTO_MATERIAL: readonly BlocoDeImposto[] = [
  {
    rotulo: 'IPI',
    base: 'valorBaseIpi',
    basePadraoEhValorDoItem: true,
    linhas: [{ rotulo: 'IPI', aliquota: 'aliquotaIpi', valor: 'valorIpi' }],
  },
  {
    rotulo: 'ICMS',
    base: 'valorBaseIcms',
    basePadraoEhValorDoItem: true,
    linhas: [{ rotulo: 'ICMS', aliquota: 'aliquotaIcms', valor: 'valorIcms' }],
  },
  {
    rotulo: 'ICMS ST',
    base: 'valorBaseIcmsSt',
    basePadraoEhValorDoItem: true,
    linhas: [{ rotulo: 'ICMS ST', aliquota: 'aliquotaIcmsSt', valor: 'valorIcmsSt' }],
  },
  {
    rotulo: 'Crédito do Simples Nacional',
    base: null,
    basePadraoEhValorDoItem: true,
    linhas: [
      { rotulo: 'Crédito', aliquota: 'aliquotaCreditoSimples', valor: 'valorCreditoSimples' },
    ],
  },
  BLOCO_IBS_CBS,
];

export const BLOCOS_IMPOSTO_SERVICO: readonly BlocoDeImposto[] = [
  {
    rotulo: 'ISS',
    base: 'valorBaseIss',
    basePadraoEhValorDoItem: false,
    // Base e dois pares — devido e retido — não cabem em meia largura.
    linhaInteira: true,
    linhas: [
      { rotulo: 'ISS', aliquota: 'aliquotaIss', valor: 'valorIss' },
      { rotulo: 'ISS retido', aliquota: 'aliquotaIssRetido', valor: 'valorIssRetido' },
    ],
  },
  {
    rotulo: 'IRF',
    base: 'valorBaseIrf',
    basePadraoEhValorDoItem: false,
    linhas: [{ rotulo: 'IRF', aliquota: 'aliquotaIrf', valor: 'valorIrf' }],
  },
  {
    rotulo: 'INSS',
    base: 'valorBaseInss',
    basePadraoEhValorDoItem: false,
    linhas: [{ rotulo: 'INSS', aliquota: 'aliquotaInss', valor: 'valorInss' }],
  },
  {
    rotulo: 'Retenções federais',
    base: 'valorBaseRetencoes',
    basePadraoEhValorDoItem: false,
    // Base e três pares de alíquota e valor.
    linhaInteira: true,
    linhas: [
      { rotulo: 'PIS retido', aliquota: 'aliquotaRetencaoPis', valor: 'valorPisRetido' },
      { rotulo: 'COFINS retido', aliquota: 'aliquotaRetencaoCofins', valor: 'valorCofinsRetido' },
      { rotulo: 'CSLL retida', aliquota: 'aliquotaRetencaoCsll', valor: 'valorCsllRetido' },
    ],
  },
  BLOCO_IBS_CBS,
];

/**
 * Os blocos que valem para um insumo.
 *
 * Quem chama passa o `tipo` do insumo. Na **cotação** a decisão é do documento
 * inteiro — ela tem um insumo só —, e no **pedido** é de cada item: o mesmo
 * pedido compra cimento e contrata a bomba de concretagem, e as duas linhas
 * perguntam impostos diferentes.
 */
export function blocosDeImposto(tipo: TipoInsumo): readonly BlocoDeImposto[] {
  return tipo === 'SERVICO' ? BLOCOS_IMPOSTO_SERVICO : BLOCOS_IMPOSTO_MATERIAL;
}

/**
 * Todos os campos de um conjunto de blocos — base, alíquotas, reduções e
 * valores.
 *
 * **A redução entrou depois**, e a ausência dela era um defeito de verdade: esta
 * lista é o que monta o schema do formulário, a projeção do repository e a
 * conversão para o payload, e a redução ficava de fora dos três. O sintoma era
 * silencioso e caro — o comprador informava 60% de redução no IBS, o valor saía
 * certo na tela porque `CamposDeImposto` registra o campo pelo bloco, e a
 * redução era descartada no `parse` do formulário. Ao reabrir o documento, o
 * valor não batia mais com a base e a alíquota, e não havia como saber por quê.
 */
export function camposDosBlocos(blocos: readonly BlocoDeImposto[]): readonly CampoDeImposto[] {
  return blocos.flatMap((bloco) => [
    ...(bloco.base === null ? [] : [bloco.base]),
    ...bloco.linhas.flatMap((linha) => [
      linha.aliquota,
      ...(linha.reducao === undefined ? [] : [linha.reducao]),
      linha.valor,
    ]),
  ]);
}

/** Todos os campos, união dos dois conjuntos. */
export const CAMPOS_DE_IMPOSTO: readonly CampoDeImposto[] = [
  ...new Set([
    ...camposDosBlocos(BLOCOS_IMPOSTO_MATERIAL),
    ...camposDosBlocos(BLOCOS_IMPOSTO_SERVICO),
  ]),
];

/**
 * Todos os blocos, dos dois conjuntos, sem repetir o de IBS e CBS.
 *
 * Serve a quem precisa olhar os impostos sem saber o tipo do insumo — o resumo
 * da linha, o "tem algo destacado?". Percorrer as duas listas concatenadas
 * visitaria IBS e CBS duas vezes, e um resumo escreveria "IBS · IBS".
 */
const TODOS_OS_BLOCOS: readonly BlocoDeImposto[] = [
  ...BLOCOS_IMPOSTO_MATERIAL,
  ...BLOCOS_IMPOSTO_SERVICO.filter((bloco) => bloco !== BLOCO_IBS_CBS),
];

/* -------------------------------------------------------------------------- */
/* Aritmética                                                                 */
/* -------------------------------------------------------------------------- */

/** `100` na escala do percentual — o divisor de `valorPorAliquota`. */
const CEM_ESCALADO = 100n * 10n ** BigInt(CASAS_PERCENTUAL);

/**
 * `base × alíquota ÷ 100`, com a redução aplicada, arredondado a centavo.
 *
 * É a conta que a tela faz enquanto o comprador digita, e a mesma que o rateio
 * proporcional refaz sobre a base nova quando o pedido compra menos do que a
 * proposta ofertou. Inteiros escalados, e não `Number`: a alíquota tem quatro
 * casas e o dinheiro tem duas, e a multiplicação em ponto flutuante erra o
 * centavo em valores grandes.
 *
 * **A redução entra na mesma divisão**, e não como um segundo arredondamento
 * sobre o resultado. Reduzir a base e depois aplicar a alíquota daria o mesmo
 * número em aritmética exata, mas em duas etapas cada uma arredondando a centavo
 * o resultado pode diferir de um centavo do que a nota destaca — e é justamente
 * o número que o comprador vai conferir contra o papel.
 *
 * Devolve `null` quando não há o que calcular — base ou alíquota ainda vazias.
 * Nesse caso a tela deixa o campo de valor como está, em vez de apagá-lo no meio
 * da digitação. A redução em branco vale zero: não reduzir é o caso comum.
 */
export function valorPorAliquota(base: string, aliquota: string, reducao = ''): string | null {
  if (base.trim() === '' || aliquota.trim() === '') return null;

  const fator = CEM_ESCALADO - escalar(reducao.trim() === '' ? '0' : reducao, CASAS_PERCENTUAL);
  const produto = escalar(base, CASAS_DINHEIRO) * escalar(aliquota, CASAS_PERCENTUAL) * fator;

  return desescalar(dividirArredondando(produto, CEM_ESCALADO * CEM_ESCALADO), CASAS_DINHEIRO);
}

/**
 * A base do ICMS: o valor do item, mais o IPI quando ele compõe.
 *
 * Quem decide é a bandeira `ipiNaBaseDoIcms`, porque quem sabe é o fornecedor:
 * na venda a consumidor final o IPI entra na base do ICMS; na venda para revenda
 * ou industrialização, não. Não há como derivar isso do cadastro do insumo nem
 * da empresa.
 *
 * Vive no contrato, e não na tela, porque é regra de cálculo — a tela a usa para
 * preencher o campo enquanto se digita, e o mesmo número precisa sair daqui no
 * dia em que a nota de compra refizer a conta.
 */
export function baseDoIcms(valorDoItem: string, valorIpi: string, ipiNaBase: boolean): string {
  if (!ipiNaBase) return valorDoItem;

  return somarDinheiro([valorDoItem, valorIpi]);
}

/** Impostos todos zerados — o documento sem tributação destacada. */
export function impostosZerados(): ImpostosDestacados {
  return {
    ...(Object.fromEntries(CAMPOS_DE_IMPOSTO.map((campo) => [campo, '0.00'])) as Record<
      CampoDeImposto,
      string
    >),
    ipiNaBaseDoIcms: false,
  };
}

/**
 * Há algum imposto destacado?
 *
 * A tela usa isto para decidir se abre o bloco recolhido: quem reabre um
 * documento com ICMS ST digitado precisa ver que ele está lá, e um bloco fechado
 * esconderia justamente o número que explica por que aquela compra não é a mais
 * barata.
 *
 * Olha **só os valores**, e não as bases nem as alíquotas: base preenchida com
 * alíquota zerada é imposto que o fornecedor não cobrou, e abrir o bloco por
 * causa dela mostraria uma seção de zeros.
 */
export function temImpostoDestacado(impostos: ImpostosDestacados | undefined): boolean {
  if (!impostos) return false;

  return TODOS_OS_BLOCOS.some((bloco) =>
    bloco.linhas.some((linha) => Number(impostos[linha.valor]) > 0),
  );
}

/**
 * Os mesmos impostos, sobre **parte** da quantidade.
 *
 * É a razão de este módulo existir. A proposta ofertou 100 sacos com ICMS de 18%
 * sobre R$ 5.000; o comprador pede 60, ou pediu 40 na semana passada e agora
 * fecha o resto. O pedido gerado precisa do imposto daquele pedaço, e há dois
 * jeitos de chegar nele:
 *
 * - **Regra de três sobre o valor destacado.** Erra centavos, porque o valor já
 *   veio arredondado, e o erro não é auditável: ninguém sabe dizer se os R$
 *   540,00 são 18% de alguma coisa ou 60% de R$ 900,00.
 * - **Proporcionalizar a base e refazer a conta pela alíquota.** É o que esta
 *   função faz. O resultado é o mesmo número que o fornecedor destacaria numa
 *   nota de 60 sacos, e ele continua explicável — base e alíquota viajam junto.
 *
 * **As alíquotas não mudam**, e é o ponto: alíquota é uma taxa, não uma parcela.
 * Só bases e valores são proporcionalizados. A bandeira do IPI na base do ICMS
 * atravessa como está, porque ela é um fato da operação e não uma quantidade.
 *
 * O valor sai de `valorPorAliquota` sobre a base nova, **exceto quando a
 * alíquota é zero**: aí ele é proporcionalizado direto. É o caso do fornecedor
 * que destacou o valor sem informar a alíquota — refazer pela alíquota zerada
 * apagaria um imposto que ele cobrou.
 *
 * `fator` é a razão entre a quantidade pedida e a ofertada, como duas strings
 * decimais. Igual a um — o caso comum, em que o pedido leva a proposta inteira —
 * devolve os impostos sem tocar em nada, e sem passar por arredondamento nenhum.
 */
export function impostosProporcionais(
  impostos: ImpostosDestacados,
  quantidadePedida: string,
  quantidadeOfertada: string,
): ImpostosDestacados {
  const pedida = escalar(quantidadePedida, CASAS_QUANTIDADE);
  const ofertada = escalar(quantidadeOfertada, CASAS_QUANTIDADE);

  // Proposta inteira, ou oferta zerada que não dá razão nenhuma: nada a refazer.
  // Devolver a cópia intacta é diferente de multiplicar por 1 — este caminho não
  // passa por arredondamento, e o número gravado é o que o fornecedor destacou.
  if (ofertada === 0n || pedida === ofertada) return { ...impostos };

  // A conta é `dinheiroProporcional`, em `aritmetica.ts`. Era escrita aqui, e
  // saiu quando a entrada de nota por pedido passou a precisar da mesma fatia
  // para frete, "outros" e desconto: duas cópias arredondariam igual até o dia
  // em que uma delas fosse corrigida.
  const proporcional = (valor: string): string =>
    dinheiroProporcional(valor, quantidadePedida, quantidadeOfertada);

  const resultado: Record<string, string | boolean> = { ...impostos };

  for (const bloco of TODOS_OS_BLOCOS) {
    /*
      A base do bloco encolhe primeiro, porque é dela que os valores saem.

      O crédito do Simples não tem campo de base — a base dele é o valor do item,
      que já vem proporcional no item do pedido —, e por isso o `null` aqui não é
      exceção a tratar: é o bloco dizendo que não há base a encolher.
    */
    const baseNova = bloco.base === null ? null : proporcional(impostos[bloco.base]);
    if (bloco.base !== null && baseNova !== null) resultado[bloco.base] = baseNova;

    for (const linha of bloco.linhas) {
      const aliquota = impostos[linha.aliquota];
      const reducao = linha.reducao === undefined ? '' : impostos[linha.reducao];

      const recalculado =
        baseNova === null || Number(aliquota) === 0
          ? null
          : valorPorAliquota(baseNova, aliquota, reducao);

      resultado[linha.valor] = recalculado ?? proporcional(impostos[linha.valor]);
    }
  }

  return resultado as ImpostosDestacados;
}
